// Moving a lead to Lost asks why, in a small centred dialog: the reason is kept on the lead and shows up in the funnel.

import type { LeadLostReason } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Button, Dialog, RadioGroup, useToast } from '@clavian/ui-web';
import { useState, type ReactNode } from 'react';

import { useProblem } from '../../api/use-problem';
import { Notice } from '../../ui';
import { LEAD_LOST_REASON_ORDER } from './labels';
import { useChangeLeadStage } from './queries';

export interface LostReasonDialogProps {
  /** The lead being marked lost; `undefined` keeps the dialog closed. */
  readonly lead: { readonly id: string; readonly businessName: string } | undefined;
  readonly onClose: () => void;
}

export function LostReasonDialog({ lead, onClose }: LostReasonDialogProps): ReactNode {
  const { t } = useTranslation('hq');
  return (
    <Dialog open={lead !== undefined} onOpenChange={(open) => (open ? undefined : onClose())} size="sm" title={t('leads.lost.title')} description={lead?.businessName}>
      {lead === undefined ? null : <LostReasonForm lead={lead} onDone={onClose} />}
    </Dialog>
  );
}

function LostReasonForm({ lead, onDone }: { readonly lead: { readonly id: string; readonly businessName: string }; readonly onDone: () => void }): ReactNode {
  const { t } = useTranslation('hq');
  const problems = useProblem();
  const { show } = useToast();
  const change = useChangeLeadStage();
  const [reason, setReason] = useState<LeadLostReason | undefined>(undefined);
  const [failure, setFailure] = useState<string | undefined>(undefined);

  const confirm = async () => {
    if (reason === undefined) return;
    setFailure(undefined);
    try {
      await change.mutateAsync({ leadId: lead.id, body: { stage: 'lost', lostReason: reason } });
      show({ tone: 'ok', title: t('leads.pipeline.moved', { name: lead.businessName, stage: t('leads.stage.lost') }) });
      onDone();
    } catch (error) {
      setFailure(problems.text(error, { fallback: t('leads.lost.failed') }));
    }
  };

  return (
    <form
      className="console-form"
      onSubmit={(event) => {
        event.preventDefault();
        void confirm();
      }}
    >
      <p className="console-muted">{t('leads.lost.description')}</p>
      {failure === undefined ? null : (
        <Notice tone="danger" role="alert">
          {failure}
        </Notice>
      )}
      <RadioGroup
        label={t('leads.lost.reason')}
        name="lostReason"
        value={reason ?? ''}
        onValueChange={(value) => setReason(value as LeadLostReason)}
        options={LEAD_LOST_REASON_ORDER.map((one) => ({ value: one, label: t(`leads.lostReason.${one}`) }))}
      />
      <div className="console-form__actions">
        <Button type="button" variant="ghost" onClick={onDone}>
          {t('leads.lost.cancel')}
        </Button>
        <Button type="submit" variant="primary" disabled={reason === undefined} busy={change.isPending}>
          {t('leads.lost.confirm')}
        </Button>
      </div>
    </form>
  );
}
